import httpResource from "@/components/infrastructures/functions/http/httpResource";
import {
    AuthPolicy,
    legacyPolicy,
    parseAuthPolicy,
    POLICY_TIMEOUT_MS,
    tokenFromHash
} from "@/components/domains/account/passwordless/authPolicy";

/** バックエンドの client キー（この画面の着地 URL をバックエンドが選ぶのに使う）。 */
export const CLIENT_KEY = "legacy";

let cached: AuthPolicy | null = null;
let inflight: Promise<AuthPolicy> | null = null;

/**
 * GET /auth/policy。404・失敗・4 秒以内に返らないときは、これまでの動作（パスワードのみ）にする。
 * ログイン系の画面は fresh: true で毎回取り直す（切り替え日時を越えてもデプロイなしで変わるため）。
 */
export function loadAuthPolicy(opts: { fresh?: boolean } = {}): Promise<AuthPolicy> {
    if (!opts.fresh) {
        if (cached) {
            return Promise.resolve(cached);
        }
        if (inflight) {
            return inflight;
        }
    }
    const p: Promise<AuthPolicy> = new Promise<AuthPolicy>((resolve) => {
        let settled = false;
        const finish = (policy: AuthPolicy) => {
            if (settled) {
                return;
            }
            settled = true;
            resolve(policy);
        };
        const timer = setTimeout(() => finish(legacyPolicy()), POLICY_TIMEOUT_MS);
        // handlerEnabled: false … 401/403 時のログアウト・画面移動の共通処理を通さない。
        httpResource
            .get("/auth/policy", {timeout: POLICY_TIMEOUT_MS, handlerEnabled: false} as any)
            .then((res: any) => finish(res && res.status === 200 ? parseAuthPolicy(res.data) : legacyPolicy()))
            .catch(() => finish(legacyPolicy()))
            .then(() => clearTimeout(timer));
    }).then((policy) => {
        cached = policy;
        return policy;
    });
    inflight = p;
    return p;
}

/** 直近に取得した方針（まだなければ null）。 */
export function cachedAuthPolicy(): AuthPolicy | null {
    return cached;
}

/** AuthErrorResponse { errorCode, status, message, reason } から reason を取り出す。 */
export function errorReason(error: any): string {
    const data = error && error.response && error.response.data;
    return data && typeof data.reason === "string" ? data.reason : "";
}

/** AuthErrorResponse の message（そのまま利用者に見せてよい日本語）。なければ fallback。 */
export function errorMessage(error: any, fallback: string): string {
    const data = error && error.response && error.response.data;
    if (data && typeof data.message === "string" && data.message.trim() !== "") {
        return data.message;
    }
    return fallback;
}

/**
 * メールのリンク（…#token=abc）から token を読み、アドレスバーから # 以降を消す。
 * history.state はそのまま渡す（vue-router が持つ state を壊さないため）。hashchange は発生しない。
 */
export function takeFragmentToken(): string {
    const hash = window.location.hash || "";
    const token = tokenFromHash(hash);
    if (hash) {
        try {
            window.history.replaceState(window.history.state, "", window.location.pathname + window.location.search);
        } catch (e) {
            // NOP
        }
    }
    return token;
}

export const FALLBACK_ERROR = "うまくいきませんでした。時間をおいて、もう一度お試しください。";

export default class PasswordlessService {

    /** 202（登録の有無にかかわらず同じ）。 */
    public static requestMagicLink(email: string) {
        return httpResource.post("/auth/magic-link", {email: email.trim(), client: CLIENT_KEY}, {handlerEnabled: false} as any);
    }

    /** 200 + Set-Cookie（/auth/login と同じ）。 */
    public static verifyMagicLink(token: string) {
        return httpResource.post("/auth/magic-link/verify", {token}, {handlerEnabled: false} as any);
    }

    /** メールアドレスのないアカウント向け。200 + Set-Cookie。 */
    public static loginWithUsername(username: string, password: string) {
        return httpResource.post("/auth/login/username", {username, password}, {handlerEnabled: false} as any);
    }

    /** ログイン中に呼ぶ。202。401 のときは共通処理でログイン画面へ戻る。 */
    public static setupMailAddress(email: string) {
        return httpResource.post("/mail-address/setup", {email: email.trim(), client: CLIENT_KEY});
    }

    /** ログインしているか（401 でもログイン画面へ移動しない）。 */
    public static async isSignedIn(): Promise<boolean> {
        try {
            const res: any = await httpResource.post("/auth/me", null, {handlerEnabled: false} as any);
            return res.status === 200 && !!res.data;
        } catch (e) {
            return false;
        }
    }

    /** ログイン不要。200 { mailAddress }。 */
    public static confirmMailAddress(token: string) {
        return httpResource.post("/auth/mail-address/confirm", {token}, {handlerEnabled: false} as any);
    }

    /** パスワードなしの新規登録。200 + Set-Cookie { userId, productId, login }。 */
    public static registerPasswordless(body: {
        path: string;
        username: string;
        host: string | null;
        agreeTermsOfService: boolean;
        agreeProvidingData: boolean;
    }) {
        return httpResource.post("/account/register/passwordless", body, {handlerEnabled: false} as any);
    }
}
